// Third Party
import * as React from 'react';
import { Formik, Form as FormikForm, FormikHelpers } from 'formik';
import { useRouter } from 'next/router';

// Utils
import { ValidationError, useUserCreateMutation } from '@gql';
import { routes } from '@routes';

// UI
import { Button } from '@components/button';
import { FormSchema, formSchema, initialValues } from './form-schema';
import { FormikInput } from '@components/form/formik-input';
import { FormikPasswordInput } from '@components/form/formik-password-input';
import { Link } from '@components/link';
import { Typography } from '@components/typography';
import { signIn } from 'next-auth/react';

type Props = {};

export const Form: React.FC<Props> = () => {
  const [userCreateMutation] = useUserCreateMutation();
  const router = useRouter();

  const onSubmit = async (
    { email, password }: FormSchema,
    formikHelpers: FormikHelpers<FormSchema>
  ) => {
    try {
      const results = await userCreateMutation({
        variables: {
          input: {
            email,
            password,
          },
        },
      });

      if (results.data?.userCreate.__typename === 'User') {
        if (!results.data?.userCreate.refreshToken)
          throw new Error('refreshToken not present');
        await signIn('EngineRefreshTokenLogin', {
          refresh_token: results.data.userCreate.refreshToken,
          redirect: false,
        });

        if (
          router.query['redirect'] &&
          typeof router.query['redirect'] === 'string'
        ) {
          window.location.replace(router.query['redirect']);
        } else {
          window.location.reload();
        }
      } else if (
        results.data?.userCreate.__typename &&
        results.data?.userCreate.__typename === 'ValidationErrorCollection'
      ) {
        formikHelpers.setErrors(
          results.data?.userCreate.errors.reduce(
            (obj: {}, item: ValidationError) => {
              if (!item.field) {
                return obj;
              } else {
                return Object.assign(obj, {
                  [item.field]: item.message,
                });
              }
            },
            {}
          )
        );
        // Clear password values
        formikHelpers.setFieldTouched('password', false, false);
        formikHelpers.setFieldValue('password', '', false);
      }
    } catch (error) {
      console.log('JOIN ERROR', error);
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={formSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting }) => {
          return (
            <FormikForm>
              <div className="grid grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="col-span-4">
                  <FormikInput
                    type="email"
                    label="Email"
                    name="email"
                    id="email"
                  />
                </div>

                <div className="col-span-4">
                  <FormikPasswordInput
                    label="Password"
                    name="password"
                    id="password"
                  />

                  <Typography component="p" className="mt-2 text-xs">
                    We enforce an 8 character minimum, and suggest avoiding
                    patterns and common words/phrases. See{' '}
                    <Link
                      tabIndex={-1}
                      to="https://support.google.com/accounts/answer/32040?hl=en"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Google&apos;s creating a strong password
                    </Link>{' '}
                    guide for more help.
                  </Typography>
                </div>

                <div className="col-span-4">
                  <Typography component="p" className="text-xs">
                    By selecting Sign Up, I agree to Savvy&apos;s{' '}
                    <Link
                      tabIndex={-1}
                      to={routes.terms}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms &amp; Conditions
                    </Link>{' '}
                    and acknowledge the{' '}
                    <Link
                      tabIndex={-1}
                      to={routes.privacy}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </Typography>
                </div>

                <div className="col-span-4 flex flex-col-reverse sm:flex-row justify-center">
                  <Button
                    data-testid="signup-form-button"
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                  >
                    <div className="flex justify-center w-full">Sign Up</div>
                  </Button>
                </div>
              </div>
            </FormikForm>
          );
        }}
      </Formik>
    </div>
  );
};
